import { useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { usePickerList } from '../../hooks/use-picker-list.js';
import { labelFor, normalizeOptions } from './picker-options.js';
import { buildSuggestions, displayValue, isSelectedSuggestion, suggestionListClass } from './combobox-helpers.js';

/**
 * @param {Object} props
 * @param {string} [props.id]
 * @param {string} [props.value]
 * @param {Array<string | { value: string, label: string }>} [props.options]
 * @param {(value: string) => void} props.onChange
 * @param {boolean} [props.selectOnly]
 * @param {boolean} [props.allowCustom]
 * @param {boolean} [props.defaultOpen]
 * @param {boolean} [props.disabled]
 * @param {boolean} [props.clearable]
 * @param {string} [props.emptyLabel]
 * @param {string} [props.placeholder]
 * @param {string} [props.title] — overrides the value-based title, also set as aria-description
 * @param {string} [props.className]
 * @param {string} [props.testId]
 * @param {string} [props.ariaLabel]
 * @param {import('react').MutableRefObject<HTMLInputElement | null>} [props.inputRef]
 * @param {import('react').MutableRefObject<{ open: () => void } | null>} [props.pickerRef]
 * @returns {JSX.Element}
 */
export function Combobox({
	id, value = '', options: rawOptions = [], onChange,
	selectOnly = false, allowCustom = true, defaultOpen, disabled = false,
	clearable = false, emptyLabel, placeholder, title, className,
	testId, ariaLabel, inputRef, pickerRef,
}) {
	const { t } = useTranslation();
	const [open, setOpen] = useState(Boolean(defaultOpen));
	const _inputRef = useRef(/** @type {HTMLInputElement | null} */ (null));
	const selectedIndexRef = useRef(-1);
	const baseId = id || 'combobox';

	const committedLabel = selectOnly ? labelFor(normalizeOptions(rawOptions), value) : value;
	const [userDraft, setUserDraft] = useState(/** @type {string | null} */ (null));
	const hasTyped = userDraft !== null;
	const draft = userDraft ?? '';
	const query = hasTyped ? draft.trim().toLowerCase() : '';

	const { suggestions } = buildSuggestions({
		selectOnly, rawOptions, value, query, hasTyped, allowCustom, clearable, emptyLabel, t,
	});

	useEffect(() => {
		selectedIndexRef.current = suggestions.findIndex((s) => isSelectedSuggestion(s, value, selectOnly));
	});

	function openPicker() {
		setOpen(true);
		const idx = selectedIndexRef.current;
		setHighlighted(idx >= 0 ? idx : 0);
	}

	useImperativeHandle(pickerRef, () => ({ open: openPicker }), []);

	// React's synthetic onFocus can be missed on iOS Safari for input taps, so also
	// listen for the native focus event to reliably open the list on touch/keyboard.
	useEffect(() => {
		const input = _inputRef.current;
		if (!input) { return () => {}; }
		const onNativeFocus = () => { if (!selectOnly || !disabled) { openPicker(); } };
		input.addEventListener('focus', onNativeFocus);
		return () => input.removeEventListener('focus', onNativeFocus);
	}, [selectOnly, disabled]);

	const onEnterFn = (i) => {
		if (i >= suggestions.length) { return; }
		onChange(selectOnly ? suggestions[i].value : suggestions[i].name);
		setUserDraft(null);
		setHighlighted(0);
		setOpen(false);
	};

	const {
		rootRef, listRef, clampHighlight, setHighlighted,
		showSuggestions, flipUp, alignRight, handleKeyDown: pickerKeyDown,
	} = usePickerList({ idPrefix: baseId, open, setOpen, suggestionCount: suggestions.length, onEnter: onEnterFn });

	/** @param {import('react').KeyboardEvent<HTMLInputElement>} e */
	function handleKeyDown(e) {
		if (selectOnly && disabled) { e.preventDefault(); return; }
		if (e.key === 'Escape') { setUserDraft(null); }
		pickerKeyDown(e);
	}

	/**
	 * Close only on a genuine focus move to an element outside the combobox
	 * (tab-away, clicking another field). A transient blur with no related
	 * target (common on touch/iOS after an auto-focus) keeps the list open;
	 * selection, Escape, and outside clicks dismiss it via other handlers.
	 * @param {import('react').FocusEvent<HTMLInputElement>} e
	 */
	function handleBlur(e) {
		const next = /** @type {Node | null} */ (e.relatedTarget);
		if (next && rootRef.current?.contains(next)) { return; }
		if (!next) { return; }
		setOpen(false);
		if (selectOnly) {
			const norm = normalizeOptions(rawOptions);
			const exact = norm.find((o) => o.label.toLowerCase() === draft.trim().toLowerCase());
			if (exact) { onChange(exact.value); }
		}
		setUserDraft(null);
	}

	function handleOptionMouseDown(s) {
		onChange(selectOnly ? s.value : s.name);
		setUserDraft(null);
		setHighlighted(0);
		setOpen(false);
	}

	function handleInputChange(e) {
		setUserDraft(e.target.value);
		if (!selectOnly) { onChange(e.target.value); }
		setOpen(true);
		setHighlighted(0);
	}

	function handleOpen() {
		if (selectOnly && disabled) { return; }
		openPicker();
	}

	const displayedValue = displayValue({ open, hasTyped, draft, selectOnly, committedLabel, value });
	const clsx = className ? ' ' + className : '';

	return (
		<div ref={rootRef} title={title} className={`combobox${clsx}${open ? ' is-open' : ''}`}>
			<input
				id={id}
				ref={(el) => { _inputRef.current = el; if (inputRef) { inputRef.current = el; } }}
				type="text"
				size={1}
				className={`text-field${clsx}`}
				value={displayedValue}
				role="combobox"
				aria-label={ariaLabel}
				aria-description={title}
				aria-haspopup="listbox"
				aria-expanded={showSuggestions}
				aria-autocomplete="list"
				aria-controls={showSuggestions ? `${baseId}-listbox` : undefined}
				aria-activedescendant={showSuggestions ? `${baseId}-option-${clampHighlight}` : undefined}
				onChange={handleInputChange}
				onFocus={handleOpen}
				onPointerDown={handleOpen}
				onMouseDown={handleOpen}
				onBlur={handleBlur}
				onKeyDown={handleKeyDown}
				placeholder={placeholder} title={title ?? displayedValue}
				disabled={selectOnly && disabled ? true : undefined} data-testid={testId}
			/>
			<span className="combobox-chevron" aria-hidden="true">
				<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
					<polyline points="6 9 12 15 18 9" />
				</svg>
			</span>
			{showSuggestions && (
				<ul
					id={`${baseId}-listbox`}
					ref={listRef}
					className={suggestionListClass(flipUp, alignRight)}
					role="listbox"
					aria-label={t('aria.pickerOptions')}
					onMouseDown={(e) => e.preventDefault()}
				>
					{suggestions.map((s, i) => {
						const isCustomRow = s.type === 'custom' || s.type === 'clear';
						const isSelected = isSelectedSuggestion(s, value, selectOnly);
						const key = s.type === 'custom' ? 'custom' : s.type === 'clear' ? '__clear__' : (s.value || s.name);
						return (
							<li
								key={key}
								id={`${baseId}-option-${i}`}
								role="option"
								tabIndex={-1}
								aria-selected={isSelected}
								className={`picker-suggestion${isCustomRow ? ' is-custom' : ''}${i === clampHighlight ? ' is-highlighted' : ''}${isSelected ? ' is-selected' : ''}`}
								title={s.label || s.name}
								onMouseDown={() => handleOptionMouseDown(s)}
								onMouseEnter={() => setHighlighted(i)}
							>
								{isSelected && (
									<svg className="picker-check" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
										<polyline points="20 6 9 17 4 12" />
									</svg>
								)}
								{s.label || s.name}
							</li>
						);
					})}
				</ul>
			)}
		</div>
	);
}
